import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/api";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import DashboardOverview from "@/pages/DashboardOverview";
import ProductsPage from "@/pages/ProductsPage";
import CategoriesPage from "@/pages/CategoriesPage";
import EmployeesPage from "@/pages/EmployeesPage";
import TablesPage from "@/pages/TablesPage";
import OrdersPage from "@/pages/OrdersPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import ModifiersPage from "@/pages/ModifiersPage";
import SizesPage from "@/pages/SizesPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import POSPage from "@/pages/POSPage";
import VendorsPage from "@/pages/VendorsPage";
import InventoryPage from "@/pages/InventoryPage";
import SpecialsPage from "@/pages/SpecialsPage";
import ServiceFeesPage from "@/pages/ServiceFeesPage";
import SchedulingPage from "@/pages/SchedulingPage";
import WebOrderPage from "@/pages/WebOrderPage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import RegisterAccountPage from "@/pages/RegisterAccountPage";
import ProtectedRoute from "@/components/ProtectedRoute";
import "@/App.css";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => setUser(userData);
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("session_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/weborder" element={<WebOrderPage />} />
      <Route path="/:slug/weborder" element={<WebOrderPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>}>
        <Route index element={<DashboardOverview />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/:productId" element={<ProductDetailPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="modifiers" element={<ModifiersPage />} />
        <Route path="sizes" element={<SizesPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:customerId" element={<CustomerDetailPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="tables" element={<TablesPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="vendors" element={<VendorsPage />} />
        <Route path="specials" element={<SpecialsPage />} />
        <Route path="service-fees" element={<ServiceFeesPage />} />
        <Route path="scheduling" element={<SchedulingPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="/pos" element={<ProtectedRoute><POSPage /></ProtectedRoute>} />
      <Route path="/platform" element={<PlatformAdminPage />} />
      <Route path="/register" element={<RegisterAccountPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
